import React, { FC, FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Card } from 'components/Card'
import { Trans, useTranslation } from 'react-i18next'
import { TextInput } from 'components/TextInput'
import { Button } from 'components/Button'
import { Stack } from 'components/Stack'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { Alert } from 'components/Alert'
import { useFormFields } from 'hooks/useFormFields'
import { Checkbox } from 'components/Checkbox'
import { Flex } from 'components/Flex'
import { Icon } from 'components/Icon'
import { useSignup } from '../../services/auth'
import { Logo } from 'features/shared/Logo'
import styles from './Signup.module.scss'

export const Signup: FC = () => {
  const { t } = useTranslation()
  const [singup, { status }] = useSignup()
  const [form, onFieldChange] = useFormFields({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    tos: false,
    privacyPolicy: false,
  })

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    singup(form)
  }

  return (
    <Flex justify="center" align="center" className={styles.signup}>
      <Card>
        <Stack space="large">
          <Stack space="small">
            <Heading size="secondary">{t('signup.title')}</Heading>
            <Text>{t('signup.subtitle')}</Text>
          </Stack>

          <form onSubmit={onFormSubmit}>
            <Stack space="medium">
              <TextInput
                name="firstName"
                type="text"
                value={form.firstName}
                onChange={onFieldChange}
                label={t('signup.form.first_name')}
                required
              />

              <TextInput
                name="lastName"
                type="text"
                value={form.lastName}
                onChange={onFieldChange}
                label={t('signup.form.last_name')}
                required
              />

              <TextInput
                name="email"
                type="email"
                value={form.email}
                onChange={onFieldChange}
                label={t('signup.form.email')}
                required
              />

              <TextInput
                name="password"
                type="password"
                value={form.password}
                onChange={onFieldChange}
                label={t('signup.form.password')}
                required
              />

              <Checkbox
                name="tos"
                label={
                  <Trans i18nKey="signup.form.tos">
                    <Link to="/terms-and-conditions">Terms and conditions</Link>
                  </Trans>
                }
                checked={form.tos}
                onChange={onFieldChange}
                className={styles.checkbox}
                required
              />

              <Checkbox
                name="privacyPolicy"
                label={
                  <Trans i18nKey="signup.form.privacy">
                    <Link to="/privacy-policy">Privacy Policy</Link>.
                  </Trans>
                }
                checked={form.privacyPolicy}
                onChange={onFieldChange}
                className={styles.checkbox}
                required
              />

              <Button type="submit" disabled={status === 'success'} loading={status === 'loading'}>
                {t('signup.form.cta')}
              </Button>

              {status === 'error' && (
                <Alert tone="critical" full>
                  {t('signup.error')}
                </Alert>
              )}

              {status === 'success' && (
                <Alert icon={<Icon name="mail-line" />} tone="positive" full>
                  {t('signup.success')}
                </Alert>
              )}
            </Stack>
          </form>

          <Text>
            {t('signup.login_instead')} <Link to="/login">{t('signup.login_instead_cta')}</Link>
          </Text>

          <Logo />
        </Stack>
      </Card>
    </Flex>
  )
}

Signup.displayName = 'Signup'
